/**
 * Le icone del menu.
 *
 * Disegnate qui invece di prese da una libreria: sono sedici, servono solo
 * nella barra laterale, e una dipendenza in più andrebbe aggiornata per
 * sempre. Sono tutte sullo stesso reticolo 24×24, solo linee, e prendono il
 * colore dal testo — così seguono il tema chiaro e scuro senza saperne
 * niente.
 *
 * **Ognuna dice una cosa diversa.** Prima le sei anagrafiche avevano lo
 * stesso quadratino, e in un menu di sedici voci l'occhio non ci si poteva
 * appoggiare: si leggeva la parola, ogni volta. Le sei ora sono la persona
 * (clienti), il camion (fornitori), la valigetta (agenti), il germoglio
 * (lead: qualcosa che può crescere), il bersaglio (prospect: qualcuno che si
 * sta puntando) e la chiave (operatori: gli unici che entrano nel programma).
 */

export type NomeIcona =
  | 'lavoro'
  | 'clienti'
  | 'fornitori'
  | 'agenti'
  | 'lead'
  | 'prospect'
  | 'operatori'
  | 'articoli'
  | 'listini'
  | 'trattative'
  | 'ordini'
  | 'documenti'
  | 'datiGenerici'
  | 'integrazioni'
  | 'parametri'
  | 'profilo'
  | 'produzione';

const DISEGNI: Record<NomeIcona, React.ReactNode> = {
  // quattro riquadri: il riepilogo
  lavoro: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  clienti: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20.2c0-3.5 3.2-5.6 7.2-5.6s7.2 2.1 7.2 5.6" />
    </>
  ),
  // il camion: la merce che arriva
  fornitori: (
    <>
      <path d="M2.5 6.5h10.5v9.5H2.5z" />
      <path d="M13 10.2h3.6l3.9 3.6v2.2H13z" />
      <circle cx="7" cy="18.2" r="1.7" />
      <circle cx="16.6" cy="18.2" r="1.7" />
    </>
  ),
  agenti: (
    <>
      <rect x="2.5" y="7.2" width="19" height="12.3" rx="1.8" />
      <path d="M9 7.2V5.4a1.6 1.6 0 0 1 1.6-1.6h2.8A1.6 1.6 0 0 1 15 5.4v1.8" />
      <path d="M2.5 12.4h19" />
    </>
  ),
  // un germoglio: un contatto che può diventare qualcosa
  lead: (
    <>
      <path d="M12 20.5v-7.2" />
      <path d="M12 13.3c0-3.4-2.8-6.2-6.2-6.2 0 3.4 2.8 6.2 6.2 6.2z" />
      <path d="M12 13.3c0-3 2.4-5.4 5.4-5.4 0 3-2.4 5.4-5.4 5.4z" />
    </>
  ),
  // un bersaglio: qualcuno che si sta puntando
  prospect: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <circle cx="12" cy="12" r="4.4" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  // la chiave: sono gli unici che entrano nel programma
  operatori: (
    <>
      <circle cx="8.4" cy="8.4" r="4.4" />
      <path d="M11.6 11.6 20 20" />
      <path d="m16.4 16.4-2 2" />
      <path d="m20 20 1.4-1.4" />
    </>
  ),
  articoli: (
    <>
      <path d="M12 2.9 20.8 7.4v9.2L12 21.1 3.2 16.6V7.4z" />
      <path d="M3.2 7.4 12 11.9l8.8-4.5" />
      <path d="M12 11.9v9.2" />
    </>
  ),
  // il cartellino del prezzo
  listini: (
    <>
      <path d="M11.4 2.8h7.3a1.9 1.9 0 0 1 1.9 1.9v7.3a1.9 1.9 0 0 1-.56 1.35l-6.7 6.7a1.9 1.9 0 0 1-2.68 0l-7.7-7.7a1.9 1.9 0 0 1 0-2.68l6.7-6.7a1.9 1.9 0 0 1 1.35-.56z" />
      <circle cx="16.6" cy="7.4" r="1.5" />
    </>
  ),
  // due fumetti: si tratta, cioè si parla
  trattative: (
    <>
      <path d="M2.6 4.4h12.2v7.6H7.2l-4.6 3.4z" />
      <path d="M8.6 15.4h8.4l4.4 3.2v-3.2h.4V8.9" />
    </>
  ),
  ordini: (
    <>
      <circle cx="9.6" cy="19.6" r="1.6" />
      <circle cx="17.6" cy="19.6" r="1.6" />
      <path d="M2.6 3.6h2.3l2.7 11.4a1.6 1.6 0 0 0 1.55 1.2h8.5a1.6 1.6 0 0 0 1.55-1.2L21 8.1H6.1" />
    </>
  ),
  documenti: (
    <>
      <path d="M14 2.9H7.6A1.6 1.6 0 0 0 6 4.5v15A1.6 1.6 0 0 0 7.6 21h8.8a1.6 1.6 0 0 0 1.6-1.5V6.9z" />
      <path d="M14 2.9v4h4" />
      <path d="M9.2 12.6h5.6M9.2 16.1h3.8" />
    </>
  ),
  // i cursori: le tabelle che si regolano
  datiGenerici: (
    <>
      <path d="M3.4 6.8h9.2M17.2 6.8h3.4" />
      <path d="M3.4 12h3.4M11 12h9.6" />
      <path d="M3.4 17.2h7.2M15 17.2h5.6" />
      <circle cx="14.9" cy="6.8" r="2.2" />
      <circle cx="8.9" cy="12" r="2.2" />
      <circle cx="12.9" cy="17.2" r="2.2" />
    </>
  ),
  // due frecce: i dati che vanno e vengono
  integrazioni: (
    <>
      <path d="M3.4 8.4h14.2l-3.4-3.4" />
      <path d="M20.6 15.6H6.4l3.4 3.4" />
    </>
  ),
  parametri: (
    <>
      <circle cx="12" cy="12" r="3.3" />
      <path d="M12 2.6v3.1M12 18.3v3.1M2.6 12h3.1M18.3 12h3.1" />
      <path d="m5.3 5.3 2.2 2.2M16.5 16.5l2.2 2.2M18.7 5.3l-2.2 2.2M7.5 16.5l-2.2 2.2" />
    </>
  ),
  profilo: (
    <>
      <circle cx="12" cy="12" r="9.1" />
      <circle cx="12" cy="9.8" r="3" />
      <path d="M6.4 18.6c1.2-2.2 3.3-3.4 5.6-3.4s4.4 1.2 5.6 3.4" />
    </>
  ),
  // la cartellina con la spunta: gli ordini da confermare
  produzione: (
    <>
      <rect x="5" y="4.5" width="14" height="16.5" rx="1.8" />
      <path d="M9 3h6v3.2H9z" />
      <path d="m8.6 13.4 2.4 2.4 4.4-4.6" />
    </>
  ),
};

/**
 * `aria-hidden` è voluto: accanto all'icona c'è già la parola, e uno
 * screen reader che legge «icona clienti, Clienti» dice due volte la stessa
 * cosa.
 */
export function Icona({ nome, dimensione = 16 }: { nome: NomeIcona; dimensione?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={dimensione}
      height={dimensione}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {DISEGNI[nome]}
    </svg>
  );
}
